import { type MessageDescriptor, type PrimitiveType } from 'react-intl';
import {
  type DateInputValue,
  type TimeInputValue,
} from '@doxyme/ui/components/foundations';
import { storage } from '@doxyme/ui/utils';
import type { InvitationFieldValue } from '../types';
import { getFormattedDate, getFormattedTime, getRoomLinkUrl } from '../helpers';
export const newLine = '\r\n';

const getFormattedDateTime = (
  dateField: DateInputValue,
  timeField: TimeInputValue,
  formatMessage: (
    descriptor: MessageDescriptor,
    values?: Record<string, PrimitiveType>,
  ) => string,
  locale: string,
): string => {
  if (dateField === null && timeField === null) {
    return '';
  }

  return formatMessage(
    { id: 'inviteViaTextMessageForm.invitation.invitationTextDateTime' },
    {
      time:
        timeField !== null
          ? `${getFormattedTime(timeField)}${dateField !== null ? ',' : ''}`
          : '',
      date: dateField !== null ? getFormattedDate(dateField, locale) : '',
    },
  );
};

const getFormattedPasscode = (
  passcode: string | null,
  formatMessage: (
    descriptor: MessageDescriptor,
    values?: Record<string, PrimitiveType>,
  ) => string,
): string => {
  if (!passcode) {
    return '';
  }

  return formatMessage(
    { id: 'inviteViaTextMessageForm.invitation.invitationTextPasscode' },
    {
      passcode: passcode,
    },
  );
};

/*
  Returns the standardised text which includes the date (optional) / time (optional) / passcode (optional) / room url e.g.
  When: 1:05am, Wed, May 22
  https://doxy-qa2.me/ommerpro
  Passcode: 12345
*/

export const getInvitationFooterText = (
  dateField: DateInputValue,
  timeField: TimeInputValue,
  roomUrlLink: string,
  locale: string,
  passcode: string | null,
  formatMessage: (
    descriptor: MessageDescriptor,
    values?: Record<string, PrimitiveType>,
  ) => string,
): string => {
  const formattedDateTimeMessage = getFormattedDateTime(
    dateField,
    timeField,
    formatMessage,
    locale,
  );

  let text = '';

  if (formattedDateTimeMessage !== '') {
    text += `${formattedDateTimeMessage}${newLine}`;
  }

  text += getRoomLinkUrl(roomUrlLink, locale);

  const passcodeText = Boolean(passcode)
    ? getFormattedPasscode(passcode, formatMessage)
    : undefined;

  if (passcodeText !== undefined) {
    text += `${newLine}${passcodeText}`;
  }

  return text;
};

export const getInvitationTextDefaultValue = (
  providerName: string,
  formatMessage: (
    descriptor: MessageDescriptor,
    values?: Record<string, PrimitiveType>,
  ) => string,
): string => {
  const defaultContent = formatMessage(
    {
      id: 'inviteViaTextMessageForm.invitation.invitationTextBoxDefaultValue',
    },
    {
      providerName,
    },
  );
  return defaultContent;
};

/**
 * Returns the full text to be sent.
 * It's a combination of the text the provider entered / default text (see getInvitationTextDefaultValue) and the footer text (see getInvitationFooterText).
 */
export const getInvitationFormattedText = (
  invitationTextFieldValue: InvitationFieldValue,
  footerText: string,
  /**
   * The string separator between the invitation text and the footer text
   */
  separator = newLine,
): string =>
  invitationTextFieldValue !== undefined
    ? `${invitationTextFieldValue}${separator}${footerText}`
    : '';

/*
  We store whatever text the provider enters in the invitation text box per locale. This text is updated in real time based on user input.
  It then gets retrieved per locale based on the chosen language from the language dropdown.
  Note: This has to match OG for backwards compatibility
*/

export const getTextInviteLocalStorageKey = (locale: string): string =>
  `account.dashboard.invite.text.${locale}`;

export const getInvitationTextFromLocalStorage = (
  locale: string,
): string | undefined =>
  storage.getItem(getTextInviteLocalStorageKey(locale), true);

export const setInvitationTextToLocalStorage = (
  locale: string,
  value: string,
): void => storage.setItem(getTextInviteLocalStorageKey(locale), value, true);

export const deleteInvitationTextFromLocalStorage = (locale: string): void =>
  storage.removeItem(getTextInviteLocalStorageKey(locale), true);
